import { useCallback } from "react";

import type { RoomStock_StockManagementQuery } from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithBackGround,
} from "@/components/features/_layouts";
import { useIsSideMenuMobileRendered } from "@/components/features/side-menu/mobile";

import { HeaderUpdateButton, RoomStockManagementForLaptop, RoomStockManagementForMobile } from "./components";
import { useInitialized } from "../model/init";

// eslint-disable-next-line import/no-restricted-paths
import type { ApolloQueryResult } from "@apollo/client";

type Props = {
	hotelID: string;
	startDt: string;
	endDt: string;
	todaysDt: string;

	serverModel: RoomStock_StockManagementQuery;
	onFetchRoomStock: ({
		input: { startDt, endDt },
	}: {
		input: {
			startDt: string;
			endDt: string;
		};
	}) => Promise<ApolloQueryResult<RoomStock_StockManagementQuery | undefined>>;
};

export const RoomStockManagementPresenter = ({
	hotelID: _hotelID,
	startDt,
	todaysDt,
	serverModel: _serverModel,
	onFetchRoomStock,
}: Props) => {
	const { serverModel, onUpdateRoomStock, roomStockDiffStore, isValid, isDirty, onRefetch } = useInitialized({
		serverModel: _serverModel,
		onFetchRoomStock,
	});

	const handleOnSelectYearOrMonth = useCallback(
		async (input: { startDt: string; endDt: string }) => {
			await onRefetch(input);
		},
		[onRefetch],
	);

	const isSideMenuMobileRendered = useIsSideMenuMobileRendered();

	useTransitionBlock(isDirty);

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader
				title="部屋在庫の設定"
				rightChildren={
					<HeaderUpdateButton isValid={isValid} isDirty={isDirty} onUpdateRoomStock={onUpdateRoomStock} />
				}
			/>

			<FeatureIslandMainWithBackGround>
				<p className="text-brand">
					※サイトコントローラーをご利用の場合、ここでの設定はサイトコントローラーによって上書きされます
				</p>
				{isSideMenuMobileRendered ? (
					<RoomStockManagementForMobile
						serverModel={serverModel}
						roomStockDiffStore={roomStockDiffStore}
						todaysDt={todaysDt}
						startDt={startDt}
						handleOnSelectYearOrMonth={handleOnSelectYearOrMonth}
					/>
				) : (
					<RoomStockManagementForLaptop
						serverModel={serverModel}
						roomStockDiffStore={roomStockDiffStore}
						todaysDt={todaysDt}
						handleOnSelectYearOrMonth={handleOnSelectYearOrMonth}
					/>
				)}
			</FeatureIslandMainWithBackGround>
		</FeatureIslandContainer>
	);
};

RoomStockManagementPresenter.displayName = "RoomStockManagementPresenter";
